import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useGetItemQuery } from '../store/docApi';
import { useUpdatePropertyMutation, useDeletePropertyMutation } from '../store/docApi';
import ChartViewer from '../components/charts/editor/ChartViewer';
import ColorChart from '../components/charts/ColorChart';
import ColorChartThumbnail from '../components/charts/ColorChartThumbnail';
import ColorwaySelector from '../Colorway/ColorwaySelector';
import ColorwayLegend from '../Colorway/ColorwayLegend';
import LayoutEdit from '../Layout/LayoutEdit';
import EditableProperty from '../components/widgets/EditableProperty';

const LayoutDetails = () => {
  let params = useParams();
  const subId = params.subId;
  const [layout, setLayout] = useState(null);
  const { data: item } = useGetItemQuery({ label: 'workspace', pk: params.pk, id: params.id });
  const [updateProperty] = useUpdatePropertyMutation();
  const [deleteProperty] = useDeletePropertyMutation();
  const navigate = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [colors, setColors] = useState(['#ffffff', '#d3d3d3', '#808080', '#3d3d3d']);
  const [colorway, setColorway] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [showGap, setShowGap] = useState(false);
  const [showColorchart, setShowColorchart] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (item && item.layouts && subId && item.layouts[subId]) {
      setLayout({ id: subId, ...item.layouts[subId] });
      if (item.layouts[subId].colors) setColors(item.layouts[subId].colors);
    }
  }, [item, subId, setColors]);

  function deleteConfirm() {
    setDeleteConfirmOpen(true);
  }

  function deleteLayout() {
    setDeleteConfirmOpen(false);
    deleteProperty({ pk: item.pk, id: item.id, name: `layouts/${subId}` });
    navigate(`/workspaces/${item.pk}/${item.id}`);
  }

  function onSaveProperty(name, value) {
    updateProperty({ label: item.label, pk: item.pk, id: item.id, name: `layouts/${subId}/${name}`, value: value });
  }

  const selectColorway = (selectedColorway) => {
    if (!selectedColorway) return;
    if (selectedColorway.chromas && selectedColorway.chromas.length > 0) {
      const colors = selectedColorway.chromas.map((chroma) => chroma.color);
      if (colors.length > 0) {
        setColors(colors);
      }
      setColorway(selectedColorway);
    }
  };

  return (
    item &&
    layout && (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {layout.name}
            </Typography>
            {layout.description && (
              <Typography variant="body2" color="text.secondary">
                {layout.description}
              </Typography>
            )}
          </Box>
          <ButtonGroup variant="outlined" size="small">
            <Tooltip title="Edit Layout">
              <Button startIcon={<EditIcon />} onClick={() => setIsEditing(!isEditing)}>
                Edit
              </Button>
            </Tooltip>
            <Tooltip title="Delete Layout">
              <Button startIcon={<DeleteIcon />} color="error" onClick={() => deleteConfirm()}>
                Delete
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>

        <EditableProperty
          item={item}
          title="Name"
          propname={`layouts/${subId}/name`}
          propvalue={layout.name}
          placeholder="Enter layout name..."
        />
        <EditableProperty
          item={item}
          title="Description"
          propname={`layouts/${subId}/description`}
          propvalue={layout.description}
          placeholder="Enter layout description..."
        />
        <EditableProperty
          item={item}
          title="Keywords"
          propname={`layouts/${subId}/keywords`}
          propvalue={layout.keywords}
          placeholder="Enter layout keywords..."
        />
        <ColorwaySelector item={item} onSelect={selectColorway} />
        {isEditing ? (
          <LayoutEdit
            item={item}
            initialLayout={layout}
            onSaveProperty={onSaveProperty}
            colors={colors}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox checked={showLegend} onChange={() => setShowLegend(!showLegend)} />}
                label="Show legend"
              />
              <FormControlLabel
                control={<Checkbox checked={showThumbnail} onChange={() => setShowThumbnail(!showThumbnail)} />}
                label="Show as thumbnail"
              />
            <FormControlLabel control={<Checkbox checked={showGap} onChange={() => setShowGap(!showGap)} />} label="Show Gap" />
            </Box>
            <FormControlLabel control={<Checkbox checked={showColorchart} onChange={() => setShowColorchart(!showColorchart)} />} label="Show as color chart" />
            <Box sx={{ display: 'flex', mt: 3 }}>
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: showGap ? 3 : 0 }}>
                    {layout.steps &&
                      layout.steps.map((row, rowIndex) => (
                        <Box key={rowIndex} sx={{ display: 'flex', flexDirection: 'row', gap: showGap ? 3 : 0 }}>
                          {row.steps &&
                            row.steps.map((step, colIndex) => {
                              // If step is a reference, resolve motif from item.motifs
                              let motif = null;
                              if (step.motif_id && item && item.motifs) {
                                motif = item.motifs[step.motif_id];
                              }
                              const displayStep = motif ? motif : step;
                              return showThumbnail ? (
                                <ColorChartThumbnail key={`${rowIndex}_${colIndex}`} data={displayStep.data} colors={colors} />
                              ) : showColorchart ? (
                                <ColorChart key={`${rowIndex}_${colIndex}`} item={displayStep} colorway={colorway} viewonly={true} />
                              ) : (
                                <ChartViewer key={`${rowIndex}_${colIndex}`} item={displayStep} colorway={colorway} />
                              );
                            })}
                        </Box>
                      ))}
                  </Box>
                  {showLegend && colorway && (
                    <Box
                      sx={{
                        pl: 4,
                        pb: 7,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end'
                      }}
                    >
                      <ColorwayLegend colorway={colorway} />
                    </Box>
                  )}
                </>
            </Box>
          </Box>
        )}

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth>
          <DialogTitle>Delete Layout</DialogTitle>
          <DialogContent>
            <Typography>Delete this layout from {item.name}?</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deleteLayout}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  );
};

export default LayoutDetails;
